import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { api, asApiError, getAccessToken, setAccessToken, setUnauthorizedHandler } from '@/services/api';
import { isWiservetAdminEmail, canAccessAcademyEmail, canOnboardClinicsEmail } from '@/config/admin';
import { decodeJwtEmail } from '@/utils/jwt';

// Result type for googleLogin — parallel to login()'s { ok } return.
export type GoogleLoginOutcome =
  | { ok: true }
  | { ok: false; needsPasswordLink: true; email: string | null }
  | { ok: false; needsPasswordLink?: false; error: string };

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  default_role: string;
  is_active: boolean;
  is_platform_admin?: boolean;
  last_login_at?: string | null;
  created_at?: string;
}

export interface AuthClinic {
  id: string;
  slug: string;
  name: string;
  country: string;
  currency: string;
  timezone: string;
  plan_tier: string;
  is_active: boolean;
  is_demo: boolean;
  // is_financial_only — modo "Wiservet Financiero" (§WF 2026-09-07): la clínica
  // usa Wiservet solo para finanzas (sin CRM). Opcional para retrocompatibilidad
  // con respuestas backend previas a 0117 (ausente ⇒ false).
  is_financial_only?: boolean;
  subscription_status?: string; // 'demo' | 'trialing' | 'active' | 'past_due' | 'cancelled'
  reception_email?: string; // destino delegación oportunidades (0066)
  has_any_credential?: boolean; // ≥1 extraction_credential activo (para onboarding wizard)
}

export interface AuthClinicSummary {
  id: string;
  slug: string;
  name: string;
  role: string;
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<AuthUser | null>(null);
  const clinic = ref<AuthClinic | null>(null);
  const clinicsAvailable = ref<AuthClinicSummary[]>([]);
  const bootstrapped = ref(false);
  const error = ref<string | null>(null);
  const _hasAnyCredential = ref(false); // espejo del campo has_any_credential del /auth/me
  const _needsTermsAcceptance = ref(false); // true si el usuario no ha aceptado todos los T&C vigentes

  const isAuthenticated = computed(() => user.value !== null);

  // D64 Sprint 5 — Impersonación. Decodea el JWT payload (no encriptado) para
  // detectar si la sesión actual es impersonada por un admin Wiservet.
  function parseJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1];
      // base64url → base64
      const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
      const json = atob(padded);
      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  const impersonatedByAdmin = computed<string | null>(() => {
    const tok = getAccessToken();
    if (!tok) return null;
    const claims = parseJwtPayload(tok);
    const flag = claims?.impersonated_by_admin;
    if (typeof flag === 'string' && flag.length > 0) return flag;
    return null;
  });
  const isImpersonating = computed(() => impersonatedByAdmin.value !== null);
  const fullName = computed(() =>
    user.value ? `${user.value.first_name} ${user.value.last_name}`.trim() : '',
  );
  const initials = computed(() => {
    if (!user.value) return '';
    const f = user.value.first_name?.[0] ?? '';
    const l = user.value.last_name?.[0] ?? '';
    return (f + l).toUpperCase() || user.value.email[0].toUpperCase();
  });
  const role = computed(() => user.value?.default_role ?? '');

  // §2.4 D72+4 — rol REAL en la clínica activa, leído del JWT (claims.role).
  // `role` (arriba) es user.default_role a nivel usuario y puede NO coincidir
  // con el rol por clínica (un user puede ser owner en una clínica y staff en
  // otra). El gate financiero usa SIEMPRE clinicRole, no role. Impersonación de
  // admin Wiservet emite claims.role='clinic_owner' → ven finanzas (correcto).
  const clinicRole = computed(() => {
    // Fuente primaria REACTIVA: el rol de la clínica activa desde
    // `clinicsAvailable` (se setea junto con `clinic` en login/fetchMe/
    // switchClinic). Es más confiable que decodificar el JWT, cuyo
    // `getAccessToken()` NO es reactivo y podía leerse antes de tiempo →
    // un dueño veía `canViewFinancials=false` por un instante y se le
    // ocultaba/borraba la pestaña Finanzas (bug reportado 2026-06-17).
    const activeId = clinic.value?.id;
    const match = activeId ? clinicsAvailable.value.find((c) => c.id === activeId) : undefined;
    // Fallback/override por JWT: necesario para impersonación de admin Wiservet
    // (claims.role='clinic_owner' → ven finanzas) y si `clinicsAvailable` aún
    // no llegó.
    const tok = getAccessToken();
    const claims = tok ? parseJwtPayload(tok) : null;
    const jwtRole = typeof claims?.role === 'string' ? claims.role : '';
    const impersonated =
      typeof claims?.impersonated_by_admin === 'string' &&
      (claims.impersonated_by_admin as string).length > 0;
    if (impersonated) return jwtRole;
    return match?.role || jwtRole;
  });

  // canViewFinancials — espejo frontend del gate backend (middleware.RequireRoles
  // + models.FinancialRoles). Solo dueño + admin ven P&L, ingresos, márgenes,
  // costos, proveedores, rentabilidad por veterinario. El backend es la
  // seguridad real (403); esto es UX (no mostrar lo que el server negaría).
  const FINANCIAL_ROLES = ['clinic_owner', 'clinic_admin'];
  const canViewFinancials = computed(() => FINANCIAL_ROLES.includes(clinicRole.value));

  const clinicId = computed(() => clinic.value?.id ?? '');
  const clinicName = computed(() => clinic.value?.name ?? '');
  const isDemo = computed(() => clinic.value?.is_demo === true);
  // isFinancialOnly — modo "Wiservet Financiero" (§WF 2026-09-07): la clínica usa
  // Wiservet solo para finanzas (sin CRM). El router bloquea las vistas clínicas
  // (pacientes/agenda/inventario/visita) redirigiendo a /app/modo-financiero, y el
  // dashboard por defecto muestra solo la tab Finanzas.
  const isFinancialOnly = computed(() => clinic.value?.is_financial_only === true);
  // true si la clínica configuró ≥1 credencial de extracción (CRM/SII/Previred).
  // Se actualiza con cada fetchMe(). false mientras no haya credenciales → muestra wizard.
  const hasCredentials = computed(() => _hasAnyCredential.value);
  // true si el usuario aún no aceptó todos los documentos legales vigentes.
  // El wizard de T&C se muestra bloqueando el acceso hasta que sea false.
  const needsTermsAcceptance = computed(() => _needsTermsAcceptance.value);
  // Trust server-computed flag first (defense-in-depth contra DevTools tamper).
  // Mirror local solo como fallback si backend no envía el flag (transición W12+).
  const isWiservetAdmin = computed(() => {
    if (user.value?.is_platform_admin === true) return true;
    return isWiservetAdminEmail(user.value?.email ?? null);
  });

  // Acceso ACOTADO a la Academia (curso de ventas): superadmins Wiservet o el
  // allowlist de academia (ej. ventas). NO da acceso al panel admin ni a datos.
  const canAccessAcademy = computed(() => {
    if (isWiservetAdmin.value) return true;
    return canAccessAcademyEmail(user.value?.email ?? null);
  });

  // Acceso ACOTADO de alta de clínicas (onboarding): superadmins Wiservet o el
  // allowlist de onboarding (ej. Sofía/ventas). Sólo habilita "Nueva clínica" —
  // NO da acceso al panel admin ni a datos de clínicas existentes.
  const canOnboardClinics = computed(() => {
    if (isWiservetAdmin.value) return true;
    return canOnboardClinicsEmail(user.value?.email ?? null);
  });

  // ---------------------------------------------------------------------------
  // Clínica activa automática (fix login multi-clínica sin selección)
  // ---------------------------------------------------------------------------
  // Un user con 2+ clínicas que entra sin clinic_slug recibe del backend un JWT
  // SIN clinic_id (respuesta con clinic=null + clinics_available). Pero /auth/me
  // hidrata `current_clinic` con un pick de respaldo del backend, así que el
  // sidebar mostraba una clínica como activa aunque el token no la tuviera, y los
  // widgets fallaban con 400 NO_CLINIC_CONTEXT. Acá detectamos ese estado y
  // activamos una clínica real con switchClinic, que reemite el token con el
  // clinic_id en los claims.
  const LAST_CLINIC_KEY = 'wiservet:last_clinic_id';
  const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

  // clinic_id efectivo del access token ('' si el JWT no trae clínica activa).
  function tokenClinicId(): string {
    const tok = getAccessToken();
    if (!tok) return '';
    const claims = parseJwtPayload(tok);
    const cid = claims?.clinic_id;
    if (typeof cid !== 'string' || cid === '' || cid === ZERO_UUID) return '';
    return cid;
  }

  // "Última clínica usada": permite volver a la de siempre al re-loguear en
  // multi-clínica en vez de saltar siempre a la primera del listado.
  function rememberClinic(id: string): void {
    try {
      localStorage.setItem(LAST_CLINIC_KEY, id);
    } catch {
      // localStorage no disponible (modo privado) — best-effort.
    }
  }
  function lastUsedClinicId(): string {
    try {
      return localStorage.getItem(LAST_CLINIC_KEY) ?? '';
    } catch {
      return '';
    }
  }

  // Si el token NO trae clínica pero hay clínicas disponibles, activa una
  // (última usada si sigue disponible, si no la primera). No-op cuando ya hay
  // clínica en el token o cuando se está impersonando (el token de impersonación
  // SIEMPRE trae clinic_id, así que no se toca). Si no se logra activar ninguna,
  // deja `clinic` en null para no mostrar como activa una clínica que el token
  // no tiene.
  async function ensureActiveClinic(): Promise<void> {
    if (tokenClinicId()) return;
    if (impersonatedByAdmin.value) return;
    const available = clinicsAvailable.value;
    if (available.length === 0) {
      clinic.value = null;
      return;
    }
    const last = lastUsedClinicId();
    const target = available.find((c) => c.id === last) ?? available[0];
    const res = await switchClinic({ clinicId: target.id });
    if (!res.ok) clinic.value = null;
  }

  async function login(email: string, password: string, clinicSlug: string) {
    error.value = null;
    try {
      const resp = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: AuthUser;
        clinic: AuthClinic;
        clinics_available?: AuthClinicSummary[];
      }>('/auth/login', { email, password, clinic_slug: clinicSlug });
      setAccessToken(resp.data.access_token);
      user.value = resp.data.user;
      clinic.value = resp.data.clinic;
      clinicsAvailable.value = resp.data.clinics_available ?? [];
      if (clinic.value?.id) rememberClinic(clinic.value.id);
      await ensureActiveClinic();
      return { ok: true as const };
    } catch (e) {
      const err = asApiError(e);
      error.value =
        err.code === 'INVALID_CREDENTIALS'
          ? 'Email o contraseña incorrectos'
          : err.code === 'CLINIC_NOT_FOUND'
            ? 'Clínica no disponible'
            : err.code === 'USER_LOCKED'
              ? 'Demasiados intentos, intenta más tarde'
              : err.message;
      return { ok: false as const, error: error.value };
    }
  }

  async function signup(payload: {
    email: string;
    password: string;
    clinic_name?: string;
    city?: string;
    role?: string;
    clinic_size?: string;
    phone?: string;
    source?: string;
    referral_code?: string;
    terms_accepted?: boolean;
    terms_version?: string;
    // D61: granular consents (Ley 21.719 / LFPDPPP / Ley 29733).
    use_nova?: boolean;
    share_benchmarks?: boolean;
    receive_tips?: boolean;
  }) {
    error.value = null;
    try {
      const resp = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: AuthUser;
        clinic: AuthClinic;
        clinics_available?: AuthClinicSummary[];
      }>('/onboarding/signup', payload);
      setAccessToken(resp.data.access_token);
      user.value = resp.data.user;
      clinic.value = resp.data.clinic;
      clinicsAvailable.value = resp.data.clinics_available ?? [];
      if (clinic.value?.id) rememberClinic(clinic.value.id);
      // D54: marcar bootstrapped=true para evitar que el router guard
      // re-ejecute bootstrap() en el router.push('/app/bandeja') posterior.
      // Sin esto, bootstrap() llama fetchMe() redundante que en race
      // condition puede limpiar user.value → guard ve !isAuthenticated
      // → redirect silencioso a /login → user atascado en /onboarding.
      // Síntoma reportado: "Entrar a la demo" no hace nada.
      bootstrapped.value = true;
      return { ok: true as const };
    } catch (e) {
      const err = asApiError(e);
      error.value =
        err.code === 'EMAIL_EXISTS'
          ? 'Ese email ya tiene cuenta. Inicia sesión.'
          : err.code === 'VALIDATION_ERROR'
            ? err.message
            : err.code === 'SIGNUP_UNAVAILABLE'
              ? 'Signup no disponible en este entorno'
              : err.message;
      return { ok: false as const, code: err.code, error: error.value };
    }
  }

  async function fetchMe() {
    try {
      const resp = await api.get<{
        user: AuthUser;
        current_clinic: AuthClinic;
        clinics_available?: AuthClinicSummary[];
        has_any_credential?: boolean;
        needs_terms_acceptance?: boolean;
      }>('/auth/me');
      user.value = resp.data.user;
      clinic.value = resp.data.current_clinic;
      clinicsAvailable.value = resp.data.clinics_available ?? [];
      _hasAnyCredential.value = resp.data.has_any_credential ?? false;
      _needsTermsAcceptance.value = resp.data.needs_terms_acceptance ?? false;
      // Recordar la clínica solo si el TOKEN la tiene activa (una clínica
      // hidratada por el pick de respaldo del backend no cuenta como "usada").
      if (clinic.value?.id && tokenClinicId() && !impersonatedByAdmin.value) {
        rememberClinic(clinic.value.id);
      }
      // Si el token quedó sin clínica (login multi-clínica sin selección),
      // activar una real para que los widgets dejen de fallar con
      // NO_CLINIC_CONTEXT.
      await ensureActiveClinic();
      return true;
    } catch {
      return false;
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    error.value = null;
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return { ok: true as const };
    } catch (e) {
      const err = asApiError(e);
      let msg = 'No se pudo cambiar la contraseña';
      if (err.code === 'INVALID_CREDENTIALS') msg = 'Contraseña actual incorrecta';
      else if (err.code === 'WEAK_PASSWORD') msg = 'La nueva contraseña debe tener al menos 8 caracteres';
      error.value = msg;
      return { ok: false as const, error: msg };
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignorar errores de logout — limpiamos local de cualquier forma
    }
    user.value = null;
    clinic.value = null;
    clinicsAvailable.value = [];
    _hasAnyCredential.value = false;
    _needsTermsAcceptance.value = false;
    setAccessToken(null);
  }

  // D45 multi-clinic switcher: cambia la clinic activa sin re-login.
  // Acepta clinic_id O clinic_slug. Genera nuevo access token con
  // clinic_id distinto en los claims, mantiene la misma sesión.
  async function switchClinic(target: { clinicId?: string; clinicSlug?: string }) {
    if (!target.clinicId && !target.clinicSlug) {
      return { ok: false as const, error: 'clinic_id o clinic_slug requerido' };
    }
    error.value = null;
    try {
      const resp = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user?: AuthUser;
        clinic?: AuthClinic;
        clinics_available?: AuthClinicSummary[];
        impersonation?: boolean;
      }>('/auth/switch-clinic', {
        clinic_id: target.clinicId ?? '',
        clinic_slug: target.clinicSlug ?? '',
      });
      setAccessToken(resp.data.access_token);
      if (resp.data.impersonation) {
        // Admin Wiservet impersonando clínica no-miembro: fetchMe hidrata todo.
        await fetchMe();
      } else {
        user.value = resp.data.user ?? user.value;
        clinic.value = resp.data.clinic ?? clinic.value;
        clinicsAvailable.value = resp.data.clinics_available ?? [];
        if (resp.data.clinic?.id) rememberClinic(resp.data.clinic.id);
      }
      return { ok: true as const };
    } catch (e) {
      const err = asApiError(e);
      error.value =
        err.code === 'CLINIC_NOT_FOUND'
          ? 'No tienes acceso a esa clínica'
          : err.message;
      return { ok: false as const, error: error.value };
    }
  }

  // D64 Sprint 5 — Iniciar impersonación de una clínica.
  //
  // Guarda el JWT original en sessionStorage (`wiservet.original_token`)
  // para que el admin pueda "volver" sin re-loguear (próxima iteración).
  // Reemplaza el access token con el de impersonación + rehidrata user+clinic
  // via fetchMe.
  async function impersonateClinic(clinicId: string): Promise<{ ok: true; clinicName: string } | { ok: false; error: string }> {
    error.value = null;
    try {
      const original = getAccessToken();
      if (original) {
        sessionStorage.setItem('wiservet.original_token', original);
      }
      const resp = await api.post<{
        token: string;
        clinic_id: string;
        clinic_name: string;
        expires_at: string;
        admin_email: string;
      }>(`/admin/clinics/${clinicId}/impersonate`);
      setAccessToken(resp.data.token);
      // Rehidratar user+clinic con el token nuevo.
      const ok = await fetchMe();
      if (!ok) {
        // Revertir si fetchMe falló.
        if (original) setAccessToken(original);
        return { ok: false as const, error: 'No se pudo cargar la sesión impersonada' };
      }
      return { ok: true as const, clinicName: resp.data.clinic_name };
    } catch (e) {
      const err = asApiError(e);
      // Restaurar token original si quedó setteado.
      const original = sessionStorage.getItem('wiservet.original_token');
      if (original) setAccessToken(original);
      error.value = err.message;
      return { ok: false as const, error: err.message };
    }
  }

  // Salir de impersonación.
  // Llama al backend para BORRAR la marca de impersonación de la sesión y recibir
  // un token normal sin clínica. Sin esta llamada, el refresh silencioso siguiente
  // volvía a re-impersonar la última clínica (la marca quedaba persistida en la DB).
  // El token guardado en sessionStorage puede estar expirado tras una sesión larga,
  // así que ya NO dependemos de restaurarlo — el backend emite uno fresco.
  async function exitImpersonation(): Promise<void> {
    sessionStorage.removeItem('wiservet.original_token');
    try {
      const resp = await api.post<{ access_token: string }>('/admin/clinics/impersonate/exit');
      setAccessToken(resp.data.access_token);
      await fetchMe();
    } catch {
      // Si el backend falla, forzar logout para no quedar en estado inconsistente.
      await logout();
    }
  }

  async function bootstrap() {
    if (bootstrapped.value) return;
    if (getAccessToken()) {
      const ok = await fetchMe();
      if (!ok) setAccessToken(null);
    }
    bootstrapped.value = true;
  }

  // Merge parcial sobre la clínica activa (ej. tras guardar reception_email en
  // Mi Clínica) para que el resto de la app lea el valor nuevo sin re-login.
  function patchClinic(p: Partial<AuthClinic>): void {
    if (clinic.value) clinic.value = { ...clinic.value, ...p };
  }

  // Registra la aceptación de un documento de T&C. El wizard llama a esto
  // una vez por documento. Al aceptar todos, el backend devuelve
  // needs_terms_acceptance = false en el próximo fetchMe().
  async function acceptTerms(document: 'terms' | 'privacy' | 'data_processing'): Promise<{ ok: boolean }> {
    try {
      await api.post('/auth/accept-terms', { document });
      return { ok: true };
    } catch {
      return { ok: false };
    }
  }

  // Marca localmente que el usuario ya aceptó los T&C (llamado al completar el wizard).
  function markTermsAccepted(): void {
    _needsTermsAcceptance.value = false;
  }

  // ---------------------------------------------------------------------------
  // Google SSO (GIS id_token flow)
  // ---------------------------------------------------------------------------

  /**
   * Fetch a short-lived nonce from the backend.
   * The backend also sets an httpOnly g_nonce cookie for later verification.
   * Pass the returned nonce to google.accounts.id.initialize({ nonce }).
   */
  async function fetchGoogleNonce(): Promise<string> {
    const resp = await api.get<{ nonce: string }>('/auth/google/nonce');
    return resp.data.nonce;
  }

  /**
   * Sign in with a Google id_token obtained from GIS.
   * Mirrors login()'s post-success state handling exactly.
   * Returns GoogleLoginOutcome to let the view handle NEEDS_PASSWORD_LINK.
   */
  async function googleLogin(idToken: string, clinicSlug = ''): Promise<GoogleLoginOutcome> {
    error.value = null;
    try {
      const resp = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: AuthUser;
        clinic: AuthClinic;
        clinics_available?: AuthClinicSummary[];
      }>('/auth/google', { id_token: idToken, clinic_slug: clinicSlug });
      setAccessToken(resp.data.access_token);
      user.value = resp.data.user;
      clinic.value = resp.data.clinic;
      clinicsAvailable.value = resp.data.clinics_available ?? [];
      if (clinic.value?.id) rememberClinic(clinic.value.id);
      await ensureActiveClinic();
      return { ok: true };
    } catch (e) {
      const err = asApiError(e);
      if (err.code === 'NEEDS_PASSWORD_LINK') {
        // Backend deliberately does NOT return email (no PII re-emission).
        // Decode display-only email client-side from the id_token payload.
        return { ok: false, needsPasswordLink: true, email: decodeJwtEmail(idToken) };
      }
      error.value = err.code === 'INVALID_CREDENTIALS'
        ? 'No encontramos una cuenta activa con ese correo de Google'
        : err.message;
      return { ok: false, error: error.value };
    }
  }

  /**
   * Link Google to an existing account that lacks email_verified_at,
   * by proving identity with the user's current password.
   * On success, mirrors login()'s post-login state handling.
   */
  async function linkGoogleWithPassword(
    idToken: string,
    password: string,
    clinicSlug = '',
  ): Promise<GoogleLoginOutcome> {
    error.value = null;
    try {
      const resp = await api.post<{
        access_token: string;
        token_type: string;
        expires_in: number;
        user: AuthUser;
        clinic: AuthClinic;
        clinics_available?: AuthClinicSummary[];
      }>('/auth/google/link-with-password', { id_token: idToken, password, clinic_slug: clinicSlug });
      setAccessToken(resp.data.access_token);
      user.value = resp.data.user;
      clinic.value = resp.data.clinic;
      clinicsAvailable.value = resp.data.clinics_available ?? [];
      if (clinic.value?.id) rememberClinic(clinic.value.id);
      await ensureActiveClinic();
      return { ok: true };
    } catch (e) {
      const err = asApiError(e);
      error.value = err.code === 'INVALID_CREDENTIALS'
        ? 'Contraseña incorrecta'
        : err.message;
      return { ok: false, error: error.value };
    }
  }

  // Configurar handler para 401 después de fallback de refresh
  setUnauthorizedHandler(() => {
    user.value = null;
    clinic.value = null;
    clinicsAvailable.value = [];
  });

  return {
    user,
    clinic,
    clinicsAvailable,
    bootstrapped,
    error,
    isAuthenticated,
    fullName,
    initials,
    role,
    clinicRole,
    canViewFinancials,
    clinicId,
    clinicName,
    isDemo,
    isFinancialOnly,
    hasCredentials,
    needsTermsAcceptance,
    isWiservetAdmin,
    canAccessAcademy,
    canOnboardClinics,
    impersonatedByAdmin,
    isImpersonating,
    changePassword,
    login,
    signup,
    fetchMe,
    logout,
    bootstrap,
    switchClinic,
    impersonateClinic,
    exitImpersonation,
    patchClinic,
    acceptTerms,
    markTermsAccepted,
    fetchGoogleNonce,
    googleLogin,
    linkGoogleWithPassword,
  };
});
